import {
    Button,
    Dialog,
    DialogContent,
    DialogTrigger,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDownIcon, Loader2Icon, Trash } from 'lucide-react';
import { toast } from 'sonner';

import { ButtonGroup } from '@components/ui/button';

import { toastError } from '@/utils';

import {
    useDeleteUnitMutation,
    useUpdateUnitMutation,
} from '../../../../features/slice/products/unit/unit-api-slice';
import { UnitForm } from '../unit-form.component';

const ActionCell = ({ row }: CellContext<any, unknown>) => {
    const [deleteUnit, { isLoading: isDeleting }] = useDeleteUnitMutation();
    const [editUnit, { isLoading: isEditing }] = useUpdateUnitMutation();

    const handleDeleteUnit = async (unitId: string) => {
        const confirmed = window.confirm(
            'Are you sure you want to delete this unit?',
        );
        if (!confirmed) return;

        try {
            await deleteUnit(unitId).unwrap();
            toast.success('Unit has been deleted');
        } catch (error) {
            toastError(
                error,
                'useUnitList: handleDeleteUnit -> Failed to delete unit.',
            );
        }
    };

    const handleEdit = async (data: any) => {
        try {
            await editUnit({
                id: row.original.id,
                name: data.name,
                quantity: data.quantity,
                strip_quantity: data.strip_quantity,
            }).unwrap();
            toast.success('Unit has been updated');
        } catch (error) {
            toastError(error, 'Failed to edit unit.');
        }
    };

    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                <Dialog>
                    <DialogTrigger>
                        <Button type={'button'} variant={'outline'}>
                            Edit
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <UnitForm
                            initialValues={{
                                name: row.original.name,
                                quantity: row.original.quantity,
                                strip_quantity: row.original.strip_quantity,
                            }}
                            onSubmit={handleEdit}
                            title="Edit Unit"
                            isLoading={isEditing}
                        />
                    </DialogContent>
                </Dialog>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeleteUnit(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin'} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

export const unitListColumns: ColumnDef<any>[] = [
    {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
            return (
                <div
                    title={row.getValue('name')}
                    // className=" w-[200px] cursor-pointer truncate"
                >
                    {row.getValue('name')}
                </div>
            );
        },
    },

    {
        accessorKey: 'quantity',
        header: 'Quantity',
        cell: ({ row }) => {
            return (
                <div
                    title={row.getValue('quantity')}
                    // className=" w-[200px] cursor-pointer truncate"
                >
                    {row.getValue('quantity')}
                </div>
            );
        },
    },
    {
        accessorKey: 'strip_quantity',
        header: 'Strip Quantity',
        cell: ({ row }) => {
            return (
                <div
                    title={row.getValue('strip_quantity')}
                    // className=" w-[200px] cursor-pointer truncate"
                >
                    {row.getValue('strip_quantity')}
                </div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ActionCell,
    },
];
